<script lang="ts" setup>
import { onMounted, ref, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import VueTurnstile from 'vue-turnstile'
import type { UserProfileDetailsDto, UserPreferencesDto } from '@roadfootage/api-client'
import { useUserStore } from '../stores/user/user.store'
import { storeToRefs } from 'pinia'
import { useSessionStore } from '@/stores/session'
import { regionConfig } from '@/config/region'
import { formatNumber } from '@/utils/format'

const { user } = storeToRefs(useSessionStore())
const router = useRouter()
const userStore = useUserStore()
const $q = useQuasar()

// Shared
const turnstileSiteId = import.meta.env.VITE_TURNSTILE_SITE_KEY
const isLoading = ref(false)
type GetStartedStep = 'profile' | 'preferences' | 'terms' | 'credits'
const step = ref<GetStartedStep>('profile')

// Profile setup form
const profileForm: UserProfileDetailsDto = reactive({
  countryCode: regionConfig.countryCode,
  name: '',
  givenName: '',
  familyName: '',
  nickname: '',
  picture: '',
  phoneNumber: ''
})
const turnstileToken = ref<string>('')
const nickNameTouched = ref(false)

function populateName() {
  profileForm.name = `${profileForm.givenName || ''} ${profileForm.familyName || ''}`.trim()
  if (!nickNameTouched.value)
    profileForm.nickname = `${profileForm.givenName || ''}${profileForm.familyName || ''}`
      .trim()
      .toLowerCase()
}

function populateFormFromUser() {
  if (!user.value) return
  profileForm.name = user.value.name
  profileForm.givenName = user.value.given_name
  profileForm.familyName = user.value.family_name
  profileForm.nickname = user.value.nickname
  profileForm.picture = user.value.picture
  profileForm.phoneNumber = user.value.phone_number
}

async function onSubmitProfileDetails() {
  if (!turnstileToken.value) {
    $q.notify({
      color: 'red-5',
      textColor: 'white',
      icon: 'mdi-alert-box',
      message: 'Unable to verify your web browser.'
    })
    return
  }

  isLoading.value = true
  try {
    await userStore.createProfile(profileForm, turnstileToken.value as string)
  } catch (e) {
    console.error(e)
  } finally {
    isLoading.value = false
  }

  step.value = 'preferences'
}
watch(() => profileForm.givenName, populateName)
watch(() => profileForm.familyName, populateName)
watch(user, populateFormFromUser)

// Preferences
const preferencesForm: UserPreferencesDto = reactive({
  agreesToMarketing: false,
  enableEmailNotifications: true,
  enableAdvertisements: true
})
async function onSubmitPreferences() {
  isLoading.value = true
  try {
    await userStore.savePreferences(preferencesForm)
  } catch (e) {
    console.error(e)
  } finally {
    isLoading.value = false
  }

  step.value = 'terms'
}

// Terms
interface TermsForm {
  agreesToTerms: boolean
  agreesToPrivacyPolicy: boolean
}
const termsForm: TermsForm = reactive({
  agreesToTerms: false,
  agreesToPrivacyPolicy: false
})
async function onSubmitTerms() {
  if (!termsForm.agreesToTerms || !termsForm.agreesToPrivacyPolicy) {
    $q.notify({
      color: 'red-5',
      textColor: 'white',
      icon: 'mdi-alert-box',
      message: 'You must accept the privacy policy and term and conditions to continue.'
    })
    return
  }

  step.value = 'credits'
}

async function onSubmitCredits() {
  isLoading.value = true
  try {
    // Full GET /me reload gives the authoritative post-wizard state: profile,
    // preferences, and wallet together — avoids piecemeal store assembly.
    await userStore.loadProfile()
  } finally {
    isLoading.value = false
  }
  router.push({ name: 'upload' })
}

// Init
onMounted(() => {
  populateFormFromUser()
  if (!userStore.isProfileSetUp) step.value = 'profile'
  else if (!userStore.isPreferencesSetUp) step.value = 'preferences'
  else return router.push({ name: 'explore' })
})
</script>

<template>
  <q-page padding class="layout-constrained">
    <div class="q-pa-md q-col-gutter-md">
      <div class="row">
        <div class="col-12">
          <div class="text-h6">Set up your Road Footage profile.</div>
          <p class="text-body1">To get started, we need some information from you.</p>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <q-stepper v-model="step" color="primary" animated :vertical="$q.screen.lt.md">
            <q-step
              name="profile"
              title="Profile"
              icon="mdi-account"
              :done="userStore.isProfileSetUp"
              :disable="userStore.isProfileSetUp"
            >
              <q-form @submit="onSubmitProfileDetails" class="q-gutter-md">
                <div class="text-subtitle2">Your details</div>
                <q-input
                  filled
                  v-model="profileForm.givenName"
                  label="Given name *"
                  lazy-rules
                  :rules="[(val) => (val && val.length > 0) || 'Please enter your given name.']"
                />
                <q-input
                  filled
                  v-model="profileForm.familyName"
                  label="Family name *"
                  lazy-rules
                  :rules="[(val) => (val && val.length > 0) || 'Please enter your family name.']"
                />
                <q-input
                  filled
                  v-model="profileForm.nickname"
                  label="Display Name *"
                  lazy-rules
                  @change="(val: string) => (nickNameTouched = !!val)"
                  :rules="[
                    (val) =>
                      (val && val.length > 0) ||
                      'Please enter your a display name. This will appear on your public profile.'
                  ]"
                />
                <q-input filled v-model="profileForm.phoneNumber" label="Phone Number" />

                <vue-turnstile :site-key="turnstileSiteId" v-model="turnstileToken" theme="light" />

                <div>
                  <q-btn label="Submit" type="submit" color="primary" :disable="isLoading" />
                </div>
              </q-form>
            </q-step>
            <q-step
              name="preferences"
              title="Preferences"
              icon="mdi-cog"
              :done="userStore.isPreferencesSetUp"
              :disable="!userStore.isProfileSetUp || userStore.isPreferencesSetUp"
            >
              <q-form @submit="onSubmitPreferences" class="q-gutter-md">
                <div class="text-subtitle2">Your preferences</div>
                <div class="text-body2">
                  Change these preferences at any time in your account settings.
                </div>
                <div class="q-gutter-sm">
                  <q-checkbox
                    v-model="preferencesForm.agreesToMarketing"
                    label="Receive promotional communications from time to time."
                  />
                </div>
                <div class="q-gutter-sm">
                  <q-checkbox
                    v-model="preferencesForm.enableEmailNotifications"
                    label="Receive platform notifications via email."
                  />
                </div>
                <div>
                  <q-btn label="Submit" type="submit" color="primary" :disable="isLoading" />
                </div>
              </q-form>
            </q-step>
            <q-step name="terms" title="Terms" icon="mdi-file-document-outline">
              <q-form @submit="onSubmitTerms" class="q-gutter-md">
                <div class="text-subtitle2">Agreements</div>
                <div class="q-gutter-sm">
                  <q-checkbox v-model="termsForm.agreesToTerms">
                    <span>
                      I have read and accept the
                      <a
                        href="https://roadfootage.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                        >Terms of Use</a
                      >.
                    </span>
                  </q-checkbox>
                </div>

                <div class="q-gutter-sm">
                  <q-checkbox v-model="termsForm.agreesToPrivacyPolicy">
                    <span>
                      I have read and accept the
                      <a
                        href="https://roadfootage.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                        >Privacy Policy</a
                      >.
                    </span>
                  </q-checkbox>
                </div>
                <div>
                  <q-btn
                    label="Submit"
                    type="submit"
                    color="primary"
                    :disable="
                      isLoading || !termsForm.agreesToTerms || !termsForm.agreesToPrivacyPolicy
                    "
                  />
                </div>
              </q-form>
            </q-step>
            <q-step name="credits" title="Credits" icon="mdi-star-circle">
              <div class="q-gutter-md">
                <div class="text-subtitle2">You've earned your starter credits!</div>
                <p class="text-body2">
                  RoadFootage uses a points-based credits system to reward contributors and enable
                  premium features.
                </p>
                <p class="text-body2">
                  <strong>You start with {{ formatNumber(1000) }} credits.</strong>
                </p>

                <div class="text-subtitle2">Spending credits</div>
                <ul class="text-body2">
                  <li>
                    <strong>Download footage</strong> — credits are deducted when you download
                    incident footage.
                  </li>
                  <li>
                    <strong>Post a bounty</strong> — request footage from a specific time and place
                    by staking credits as a reward for matching uploads.
                  </li>
                </ul>

                <div class="text-subtitle2">Earning credits</div>
                <ul class="text-body2">
                  <li>
                    Upload dashcam footage — earn credits when your footage is approved by our
                    moderators.
                  </li>
                </ul>

                <div>
                  <q-btn
                    label="Get Started"
                    color="primary"
                    @click="onSubmitCredits"
                    :disable="isLoading"
                  />
                </div>
              </div>
            </q-step>
          </q-stepper>
        </div>
      </div>
    </div>
  </q-page>
</template>
